import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { formatScore } from '../../utils/scoreFormat';
import { getPsychologistCommentAuthor } from '../../utils/resultInsights';
import TestResultBreakdown from './TestResultBreakdown';
import { getInterpretationTone, getScoreCircleClass } from '../../utils/interpretationTone';
import '../Results/Results.css';

export default function PsychologistUserResultDetailPage() {
  const navigate = useNavigate();
  const { userId, resultId } = useParams();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getPsychologistUserTestResult(userId, resultId);
      setResult(data);
    } catch (err) {
      setResult(null);
      setError(err.message || 'Не удалось загрузить результат');
    } finally {
      setLoading(false);
    }
  }, [userId, resultId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAddComment = async (event) => {
    event.preventDefault();
    if (!commentText.trim()) {
      return;
    }
    try {
      setSubmittingComment(true);
      const response = await api.addPsychologistResultComment(userId, resultId, commentText.trim());
      setResult(response.result);
      setCommentText('');
    } catch (err) {
      setError(err.message || 'Не удалось добавить комментарий');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleExportPdf = async () => {
    try {
      setExportingPdf(true);
      await api.exportPsychologistResultPdf(userId, resultId);
    } catch (err) {
      setError(err.message || 'Ошибка экспорта PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4 text-center">
        <div className="loader-overlay">
          <img src="/CD-3.gif" alt="Загрузка..." className="loading-gif" />
        </div>
      </div>
    );
  }

  if (error && !result) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger">{error || 'Результат не найден'}</div>
        <button
          type="button"
          className="ui-btn ui-btn-ghost"
          onClick={() => navigate(`/psychologist/users/${userId}`)}
        >
          ← К профилю пользователя
        </button>
      </div>
    );
  }

  const interpretation = result.interpretation;
  const interpretationTone = getInterpretationTone(interpretation?.color);
  const comments = result.psychologistComments || [];

  return (
    <div className="container mt-4">
      <div className="result-detail-header ui-card p-3 mb-4">
        <h2>Результат теста пользователя</h2>
        <div className="d-flex gap-2 flex-wrap">
          <button
            type="button"
            className="ui-btn ui-btn-primary"
            onClick={handleExportPdf}
            disabled={exportingPdf}
          >
            {exportingPdf ? 'Формирование PDF…' : 'Скачать PDF'}
          </button>
          <button
            type="button"
            className="ui-btn ui-btn-ghost"
            onClick={() => navigate(`/psychologist/users/${userId}`)}
          >
            ← К профилю пользователя
          </button>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="result-summary-card ui-card mb-4">
        <h3>{result.testSnapshot?.title || 'Неизвестный тест'}</h3>
        {result.testSnapshot?.description && <p className="text-muted">{result.testSnapshot.description}</p>}
        {result.testSnapshot?.category && (
          <span className="badge bg-secondary mb-3">{result.testSnapshot.category}</span>
        )}

        <div className="score-display">
          <div className={`score-circle ${getScoreCircleClass(interpretation?.color)}`}>
            <div className="score-value">{formatScore(result.score)}</div>
            <div className="score-label">{interpretation?.level || 'Балл'}</div>
          </div>
        </div>

        {interpretation && (
          <div className={`interpretation-card tone-${interpretationTone}`}>
            <h4>Интерпретация результата</h4>
            <p className="interpretation-text">{interpretation.interpretation}</p>
            {interpretation.recommendation && (
              <div className="recommendation-box">
                <strong>Рекомендация:</strong>
                <p>{interpretation.recommendation}</p>
              </div>
            )}
          </div>
        )}

        <div className="result-meta">
          <p>
            <strong>Дата прохождения:</strong> {new Date(result.date).toLocaleString('ru-RU')}
          </p>
        </div>
      </div>

      <div className="ui-card p-3 mb-4">
        <h4 className="mb-3">Комментарии к результату</h4>
        {comments.length === 0 ? (
          <p className="text-muted">Комментариев пока нет.</p>
        ) : (
          <div className="exercise-grid mb-3">
            {comments.map((comment) => (
              <article className="exercise-card" key={comment._id || `${comment.createdAt}-${comment.text}`}>
                <h5>{getPsychologistCommentAuthor(comment)}</h5>
                <p className="text-muted mb-2">{new Date(comment.createdAt).toLocaleString('ru-RU')}</p>
                <p>{comment.text}</p>
              </article>
            ))}
          </div>
        )}
        <form onSubmit={handleAddComment}>
          <label htmlFor="psychologist-comment" className="form-label">Новый комментарий</label>
          <textarea
            id="psychologist-comment"
            className="form-control mb-2"
            rows={3}
            value={commentText}
            onChange={(event) => setCommentText(event.target.value)}
            placeholder="Рекомендации или наблюдения для пользователя"
            maxLength={3000}
          />
          <button
            type="submit"
            className="ui-btn ui-btn-primary"
            disabled={submittingComment || !commentText.trim()}
          >
            {submittingComment ? 'Сохранение…' : 'Добавить комментарий'}
          </button>
        </form>
      </div>

      <div className="ui-card p-3 mb-4">
        <h4 className="mb-3">Детализация ответов и шкала методики</h4>
        <TestResultBreakdown result={result} />
      </div>
    </div>
  );
}
